import { WhatsAppMessageTemplate } from "../types/loyalty.types";

/**
 * Default WhatsApp Message Templates
 * These are built-in templates that come with the system
 * Users cannot edit or delete these, but can create custom versions
 */
export const DEFAULT_WHATSAPP_TEMPLATES: WhatsAppMessageTemplate[] = [
  {
    id: "template_reward_001",
    name: "Reward Redemption Available",
    type: "reward",
    subject: "Redeem Your Rewards",
    content:
      "Hi {customerName}! 🎁 You have {points} loyalty points! Check out our exclusive rewards catalog and redeem today. Visit {restaurantName} or open our app to see all options!",
    variables: ["customerName", "points", "restaurantName"],
    description: "Send when customer has redeemable points",
    isActive: true,
    category: "rewards",
    createdAt: new Date().toISOString(),
  },
  {
    id: "template_tier_001",
    name: "Tier Upgrade Congratulations",
    type: "tier",
    subject: "Congratulations on Your New Tier!",
    content:
      "Congrats {customerName}! 🎉 You've reached {tier} tier! Enjoy {benefits} and exclusive perks. Thank you for being loyal to {restaurantName}!",
    variables: ["customerName", "tier", "benefits", "restaurantName"],
    description: "Celebrate customer tier upgrades",
    isActive: true,
    category: "tier",
    createdAt: new Date().toISOString(),
  },
  {
    id: "template_points_001",
    name: "Points Expiring Soon",
    type: "points",
    subject: "Your Points Are About to Expire!",
    content:
      "⚠️ {customerName}, you have {expiryPoints} points expiring in {daysLeft} days! Visit {restaurantName} soon to use them before they're gone!",
    variables: ["customerName", "expiryPoints", "daysLeft", "restaurantName"],
    description: "Remind customers about expiring points",
    isActive: true,
    category: "notification",
    createdAt: new Date().toISOString(),
  },
  {
    id: "template_promo_001",
    name: "Special Offer Just For You",
    type: "promotion",
    subject: "Exclusive Offer for {customerName}",
    content:
      "Exciting offer for {customerName}! 🌟 Get {discount}% off on {offerDetails}! Use code: {promoCode}. Valid until {expiryDate}! Available only at {restaurantName}.",
    variables: [
      "customerName",
      "discount",
      "offerDetails",
      "promoCode",
      "expiryDate",
      "restaurantName",
    ],
    description: "Send promotional offers to targeted customers",
    isActive: true,
    category: "promotional",
    createdAt: new Date().toISOString(),
  },
  {
    id: "template_welcome_001",
    name: "Welcome to Loyalty Program",
    type: "custom",
    subject: "Welcome!",
    content:
      "Welcome {customerName}! 👋 Thank you for joining our loyalty program at {restaurantName}. Start earning points on every order and unlock exclusive rewards!",
    variables: ["customerName", "restaurantName"],
    description: "Welcome new customers to loyalty program",
    isActive: true,
    category: "notification",
    createdAt: new Date().toISOString(),
  },
  {
    id: "template_birthday_001",
    name: "Birthday Greeting",
    type: "custom",
    subject: "Happy Birthday {customerName}! 🎂",
    content:
      "Happy Birthday {customerName}! 🎉🎂 Thank you for being a loyal customer at {restaurantName}. We hope you have a wonderful day filled with joy and celebration! Visit us soon to celebrate with a special treat! 🎈",
    variables: ["customerName", "restaurantName"],
    description: "Send birthday greetings to customers",
    isActive: true,
    category: "notification",
    createdAt: new Date().toISOString(),
  },
  {
    id: "template_birthday_002",
    name: "Birthday Special Offer",
    type: "custom",
    subject: "Special Birthday Offer for {customerName}! 🎁",
    content:
      "Happy Birthday {customerName}! 🎂 As a special birthday gift, enjoy {discount}% off on your next visit at {restaurantName}! Use code: {promoCode} and celebrate with us. Valid for {validDays} days! 🎉 Thank you for your loyalty!",
    variables: [
      "customerName",
      "restaurantName",
      "discount",
      "promoCode",
      "validDays",
    ],
    description: "Send birthday special offer to customers",
    isActive: true,
    category: "promotional",
    createdAt: new Date().toISOString(),
  },
];

/**
 * Get all templates including defaults and saved ones
 * Merges default templates with custom saved templates
 * Custom templates take precedence over defaults with same ID
 *
 * 🔧 IMPORTANT: This function auto-initializes localStorage if empty
 * Ensures templates are always available on first app load
 */
export function getAllWhatsAppTemplates(): WhatsAppMessageTemplate[] {
  const saved = localStorage.getItem("whatsapp_templates");

  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    } catch (error) {
      console.error("Failed to parse saved templates:", error);
    }
  }

  // If localStorage is empty or invalid, auto-initialize with defaults
  localStorage.setItem(
    "whatsapp_templates",
    JSON.stringify(DEFAULT_WHATSAPP_TEMPLATES)
  );

  return DEFAULT_WHATSAPP_TEMPLATES;
}

/**
 * Initialize templates in localStorage if empty
 * Called on app startup or when templates need to be reset
 */
export function initializeWhatsAppTemplates(): void {
  const saved = localStorage.getItem("whatsapp_templates");

  if (!saved) {
    // First time - save defaults
    localStorage.setItem(
      "whatsapp_templates",
      JSON.stringify(DEFAULT_WHATSAPP_TEMPLATES)
    );
  }
}
